import { formattingLocale } from "../i18n/formattingLocale";
import { LocalizedText, LocalizedElement, sourceAttribute, useSourceLanguage } from "../i18n/LocalizedCopy";
// src/realtime/ChatPanel.js
import React, { useContext, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { buildAuthHeaders } from "../forum/authHeaders";
import MemberAvatar from "../components/MemberAvatar";
import ChatReactions from "./ChatReactions";
import ChatGifPicker from "./ChatGifPicker";
import ChatImageLightbox from "./ChatImageLightbox";
import { isGameProtocolMessage } from "./chatGameProtocol";
import { downloadPracticalFile, loadPreparedReplies, uploadPrivateChatDocument } from "../seller-hub/sellerHubApi";
import "./ChatPanel.css";
import MessageTranslation from "../subscriptions/MessageTranslation";

import { LoungeContext } from "../lounge/LoungeContext";

function ChatPanel({ "data-et-copy": __etCopy = "",
  titre,
  messages,
  envoyerMessage,
  currentUsername,
  typingUsers = [],
  onStartTyping,
  onStopTyping,
  onSendReaction,
  incomingReactions = [],
  discussionBadges = [],
  hideHeader = false,
  compactTools = false,
  subtitle = "Salon en temps réel – visible par tous les êtres connectés au Temple.",
  placeholder = "Écris un message au Salon Général… (Entrée pour envoyer, Shift+Entrée pour une nouvelle ligne)",
  emptyMessage = "Aucun message pour l’instant. Ouvre le flux en envoyant le premier mot du Salon Général. ✨",
  variant = "",
  onTranslateMessage = null,
  messageTranslations = {},
  onMessageAction = null,
  privatePeer = "",
}) {
  useSourceLanguage();
  const lounge = useContext(LoungeContext);
  const MessageActions = variant === "private-premium" ? "details" : React.Fragment;
  const [sending, setSending] = useState(false);
  const [toolsExpanded, setToolsExpanded] = useState(false);
  const toolsId = useId();
  const collapsibleTools = compactTools || variant === "private-premium";
  const [texte, setTexte] = useState("");
  const [media, setMedia] = useState(null);
  const [mediaType, setMediaType] = useState("");
  const [gifUrl, setGifUrl] = useState("");
  const [gifPickerOpen, setGifPickerOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [imageZoomUrl, setImageZoomUrl] = useState("");
  const closeImageZoom = useCallback(() => setImageZoomUrl(""), []);
  const [recordingVoice, setRecordingVoice] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState("");
  const [voiceLanguage, setVoiceLanguage] = useState("en");
  const [preparedReplies, setPreparedReplies] = useState([]);
  useEffect(() => {
    let active = true;
    if (privatePeer) loadPreparedReplies().then(data => { if (active) setPreparedReplies(data.quick_replies || []); }).catch(() => {});
    return () => { active = false; };
  }, [privatePeer]);
  const attachDocument = async file => {
    if (!file) return;
    setUploading(true); setUploadError("");
    try {
      const result = await uploadPrivateChatDocument(privatePeer, file);
      setMedia(result.media_url); setMediaType(result.media_type);
      setVoiceStatus(`Document prêt : ${result.name}. Cliquez sur Envoyer pour le partager.`);
    } catch (error) { setUploadError(error.message); }
    finally { setUploading(false); }
  };
  const messagesRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const voiceStreamRef = useRef(null);
  const voiceChunksRef = useRef([]);
  const speechRecognitionRef = useRef(null);

  // 🔽 Auto-scroll en bas dès qu’un message arrive
  useEffect(() => {
    if (!messagesRef.current) return;
    messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
  }, [messages]);

  // 📌 Petite méta : nb de messages
  const totalMessages = messages?.length || 0;
  const showReactions = typeof onSendReaction === "function" || (Array.isArray(incomingReactions) && incomingReactions.length > 0);

  // 🧬 Normalisation auteur + type
  const messagesNormalises = useMemo(
    () =>
      (messages || [])
        .map((m, index) => {
        const rawAuteur =
          m.auteur ||
          m.expediteur ||
          m.author?.username ||
          m.author?.display_name ||
          m.author ||
          m.username ||
          m.user?.username ||
          m.user ||
          m.sender?.username ||
          m.sender ||
          "Anonyme";
        const auteur =
          typeof rawAuteur === "string"
            ? rawAuteur
            : String(rawAuteur?.username || rawAuteur?.display_name || "Anonyme");

        const contenu = m.contenu || m.message || m.text || "";
        if (isGameProtocolMessage(contenu)) return null;
        const media_url = m.media_url || m.payload?.media_url || "";
        const media_type = m.media_type || m.payload?.media_type || "";
        const gif_url = m.gif_url || m.payload?.gif_url || "";
        const type = m.type || "message";
        const message_kind = m.message_kind || m.type_message || "text";
        const profile_share = m.profile_share || m.payload?.profile_share || null;
        const timestamp = m.timestamp || m.created_at || m.cree_le || null;

        const estSystem = type === "system" || type === "info";
        const estSelf =
          !estSystem &&
          currentUsername &&
          auteur &&
          auteur.toLowerCase() === currentUsername.toLowerCase();

          return {
            id: m.id ?? index,
            persistedId: m.id,
            auteur,
            avatar_url: m.avatar_display_url || m.avatar_url || m.auteur_avatar || "",
            avatarPerson: [m.auteur, m.expediteur, m.author, m.user, m.sender].find((person) => person && typeof person === "object") || m,
            contenu,
            media_url,
            media_type,
            gif_url,
            type,
            message_kind,
            profile_share,
            estSystem,
            estSelf,
            timestamp,
          };
        })
        .filter(Boolean),
    [messages, currentUsername]
  );

  // 🧠 Typing : on ne montre pas ton propre pseudo
  const typingAffiches = useMemo(() => {
    if (!typingUsers || typingUsers.length === 0) return [];
    if (!currentUsername) return typingUsers;
    return typingUsers.filter(
      (u) => u && u.toLowerCase() !== currentUsername.toLowerCase()
    );
  }, [typingUsers, currentUsername]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sending || uploading || recordingVoice || (!texte.trim() && !media && !gifUrl)) return;
    setSending(true);
    setUploadError("");
    try {
    const pending = envoyerMessage({
      contenu: texte.trim(),
      media_url: media || "",
      media_type: mediaType || "",
      gif_url: gifUrl || "",
    });
    const sent = pending && typeof pending.then === "function" ? await pending : pending;
    if (sent === false) return;
    setTexte("");
    setMedia(null);
    setMediaType("");
    setGifUrl("");
    setVoiceStatus("");
    if (onStopTyping) onStopTyping();
    } catch (error) {
      setUploadError(error?.message || "Envoi impossible. Votre message est conservé.");
    } finally { setSending(false); }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleChange = (e) => {
    setTexte(e.target.value);
    if (onStartTyping) onStartTyping();
  };

  const handleBlur = () => {
    if (onStopTyping) onStopTyping();
  };

  // On s’assure de stopper le typing si le composant se démonte
  useEffect(() => {
    return () => {
      if (onStopTyping) onStopTyping();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🕒 Format HH:MM
  const formatHeure = (ts) => {
    if (!ts) return "";
    try {
      const d = new Date(ts);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleTimeString(formattingLocale(), {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  const uploadMedia = async (file, { voice = false } = {}) => {
    if (!file) return;
    setUploading(true);
    setUploadError("");
    if (voice) setVoiceStatus("Préparation du message vocal…");
    try {
      const fd = new FormData();
      fd.append("media", file);
      const res = await fetch("/api/chat/media/upload/", {
        method: "POST",
        headers: buildAuthHeaders(),
        credentials: "include",
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.detail || "Envoi de média impossible.");
      }
      if (!data.media_url) throw new Error("Le média n’a pas pu être préparé.");
      setMedia(data.media_url);
      setMediaType(data.media_type || file.type || "");
      setGifUrl("");
      setVoiceStatus(voice ? "Message vocal prêt à envoyer." : "");
    } catch (error) {
      setUploadError(error.message || "Envoi de média impossible.");
      if (voice) setVoiceStatus("");
    } finally {
      setUploading(false);
    }
  };

  const stopVoiceRecording = () => {
    try {
      if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop();
    } catch (_) {
      // Le navigateur peut avoir déjà arrêté l’enregistreur.
    }
    try {
      speechRecognitionRef.current?.stop?.();
    } catch (_) {
      // La reconnaissance est facultative.
    }
    speechRecognitionRef.current = null;
    voiceStreamRef.current?.getTracks?.().forEach((track) => track.stop());
    voiceStreamRef.current = null;
    setRecordingVoice(false);
  };

  const startVoiceRecording = async () => {
    if (recordingVoice) {
      stopVoiceRecording();
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      setVoiceStatus("L’enregistrement vocal n’est pas disponible dans ce navigateur.");
      return;
    }
    setUploadError("");
    setVoiceStatus("Autorisation du microphone…");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      voiceStreamRef.current = stream;
      const preferredTypes = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg"];
      const mimeType = preferredTypes.find((type) => window.MediaRecorder.isTypeSupported?.(type)) || "";
      const recorder = new window.MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;
      voiceChunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data?.size) voiceChunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        const finalType = recorder.mimeType || mimeType || "audio/webm";
        const blob = new Blob(voiceChunksRef.current, { type: finalType });
        voiceChunksRef.current = [];
        if (blob.size) {
          const extension = finalType.includes("ogg") ? "ogg" : "webm";
          uploadMedia(new File([blob], `message-vocal.${extension}`, { type: finalType }), { voice: true });
        }
      };
      const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (Recognition) {
        const recognition = new Recognition();
        recognition.lang = "fr-FR";
        recognition.continuous = true;
        recognition.interimResults = false;
        recognition.onresult = (event) => {
          const transcript = Array.from(event.results)
            .slice(event.resultIndex)
            .map((result) => result?.[0]?.transcript || "")
            .join(" ")
            .trim();
          if (transcript) setTexte((current) => `${current}${current ? " " : ""}${transcript}`);
        };
        recognition.start();
        speechRecognitionRef.current = recognition;
        setVoiceStatus("Enregistrement et transcription en cours…");
      } else {
        setVoiceStatus("Enregistrement en cours · transcription indisponible dans ce navigateur.");
      }
      recorder.start(250);
      setRecordingVoice(true);
    } catch (_) {
      setVoiceStatus("Le microphone n’a pas pu être ouvert.");
      stopVoiceRecording();
    }
  };

  const translateDraft = async () => {
    if (!texte.trim()) return;
    setVoiceStatus("Traduction contrôlée…");
    try {
      const response = await fetch("/api/chat/relationships/assistant/", {
        method: "POST",
        headers: buildAuthHeaders({ "Content-Type": "application/json" }),
        credentials: "include",
        body: JSON.stringify({ action: "translate", text: texte.trim(), target_language: voiceLanguage }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.detail || "Traduction indisponible.");
      setTexte(payload.translated_text || texte);
      setVoiceStatus(payload.explanation || "Traduction terminée.");
    } catch (error) {
      setVoiceStatus(error?.message || "Traduction indisponible.");
    }
  };

  useEffect(() => () => {
    try { mediaRecorderRef.current?.stop?.(); } catch (_) { /* no-op */ }
    try { speechRecognitionRef.current?.stop?.(); } catch (_) { /* no-op */ }
    voiceStreamRef.current?.getTracks?.().forEach((track) => track.stop());
  }, []);

  const resolveMediaUrl = (url) => {
    if (!url) return "";
    if (/^(https?:|data:|blob:)/i.test(url)) return url;
    if (url.startsWith("/")) return `${window.location.origin}${url}`;
    return `${window.location.origin}/${url}`;
  };

  const isVideoType = (mediaType = "", mediaUrl = "") => {
    const t = String(mediaType || "").toLowerCase();
    if (t.startsWith("video/") || t === "video") return true;
    return /\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i.test(mediaUrl || "");
  };

  const isAudioType = (mediaType = "", mediaUrl = "") => {
    const type = String(mediaType || "").toLowerCase();
    if (type.startsWith("audio/") || type === "audio") return true;
    return /\.(mp3|wav|m4a|aac|ogg|oga|webm)(\?.*)?$/i.test(mediaUrl || "");
  };

  return (
    <div
      className={`chat-panel${variant ? ` chat-panel--${variant}` : ""}`}
    >
      {/* 🧿 Header */}
      {!hideHeader ? (
        <div className="chat-panel-header">
          <div className="chat-panel-header__title">
            <h2>{titre}</h2>
            <p className="chat-panel-header__subtitle">{<LocalizedText source={subtitle} marked={__etCopy.split(',').includes("subtitle") || subtitle === "Salon en temps réel – visible par tous les êtres connectés au Temple."} />}</p>
          </div>
          <div className="chat-panel-header__meta">
            <span>{totalMessages}<LocalizedText source={" messages"} /></span>
          </div>
        </div>
      ) : null}
      {discussionBadges.length ? (
        <div className="chat-panel-badges" aria-live="polite">
          {discussionBadges.slice(0, 4).map((badge) => (
            <span key={`${badge.id}-${badge.friend || "duo"}`} className="chat-panel-badge">
              <span aria-hidden="true">{badge.badge || "✨"}</span>
              <strong>{badge.title || <LocalizedText source={"Complicité"} />}</strong>
              {badge.friend ? <em>@{badge.friend}</em> : null}
            </span>
          ))}
        </div>
      ) : null}

      {/* 💬 Messages */}
      <LocalizedElement elementType={"div"} copyAttributes={["aria-label"]}
        className="chat-panel-messages"
        ref={messagesRef}
        role={variant === "global-deluxe" ? "log" : undefined}
        aria-live={variant === "global-deluxe" ? "polite" : undefined}
        aria-relevant={variant === "global-deluxe" ? "additions" : undefined}
        aria-label={variant === "global-deluxe" ? sourceAttribute("Conversation du Salon Général") : undefined}
      >
        {messagesNormalises.length === 0 && (
          <div className="chat-message-group chat-message-group--system">
            <div className="chat-message chat-message--system">
              {<LocalizedText source={emptyMessage} marked={__etCopy.split(',').includes("emptyMessage") || emptyMessage === "Aucun message pour l’instant. Ouvre le flux en envoyant le premier mot du Salon Général. ✨"} />}
            </div>
          </div>
        )}

        {messagesNormalises.map((m) => {
          if (m.estSystem) {
            return (
              <div
                key={m.id}
                className="chat-message-group chat-message-group--system"
              >
                <div className="chat-message chat-message--system">
                  {m.contenu}
                </div>
              </div>
            );
          }

          const groupClass = m.estSelf
            ? "chat-message-group chat-message-group--self"
            : "chat-message-group chat-message-group--other";

          const rowClass = m.estSelf
            ? "chat-message-row chat-message-row--self"
            : "chat-message-row";

          const bubbleClass = [
            "chat-message",
            m.estSelf
              ? "chat-message--self"
              : "chat-message--other",
          ]
            .join(" ")
            .trim();

          const avatarClass = [
            "chat-message__avatar",
            m.estSelf ? "chat-message__avatar--self" : "",
          ]
            .join(" ")
            .trim();

          return (
            <div key={m.id} className={groupClass}>
              <div className={rowClass}>
                <span className={avatarClass}>
                  <MemberAvatar
                    member={{
                      ...m.avatarPerson,
                      username: m.auteur_username || (m.estSelf ? currentUsername : m.auteur),
                      display_name: m.auteur,
                      avatar_url: m.avatar_url || m.auteur_avatar || m.avatarPerson?.avatar_url || "",
                    }}
                    size="fill"
                    decorative
                    context="chat_message"
                  />
                </span>
                <div className="chat-message__content">
                  {m.message_kind === "profile_share" && m.profile_share ? (
                    <article className={`${bubbleClass} chat-profile-proposal`}>
                      <small><LocalizedText source={"PROPOSITION DE PROFIL"} /></small>
                      <p>{m.contenu}</p>
                      <div className="chat-profile-proposal__identity">
                        <span className="chat-profile-proposal__avatar">
                          <MemberAvatar
                            member={m.profile_share}
                            src={resolveMediaUrl(m.profile_share.avatar_url || "")}
                            size="fill"
                            decorative
                            context="chat_profile_share"
                          />
                        </span>
                        <span>
                          <strong>{m.profile_share.display_name || m.profile_share.username}</strong>
                          <em>@{m.profile_share.username}</em>
                        </span>
                      </div>
                      <Link
                        to={m.profile_share.url || `/profil/${encodeURIComponent(m.profile_share.username || "")}`}
                      ><LocalizedText source={"Voir le profil "} /><span aria-hidden="true">→</span>
                      </Link>
                    </article>
                  ) : (
                    <div className={bubbleClass}>{m.contenu}</div>
                  )}
                  {messageTranslations[m.id]?.text ? (
                    <div className={`${bubbleClass} chat-message--translation`}>
                      {messageTranslations[m.id].text}
                      {messageTranslations[m.id].unavailable ? <small><LocalizedText source={"Traduction indisponible · texte source conservé"} /></small> : null}
                    </div>
                  ) : null}
                  <MessageActions>
                  {variant === "private-premium" ? <summary className="private-chat-message-options"><LocalizedText source={"Options du message "} /><span aria-hidden="true">···</span></summary> : null}
                  {onTranslateMessage && m.contenu ? (
                    <button type="button" className="chat-message-translate" onClick={() => onTranslateMessage(m.id, m.contenu, voiceLanguage)}><LocalizedText source={"Traduire ce message"} /></button>
                  ) : null}
                  {privatePeer && m.persistedId && m.message_kind === "text" && m.contenu && !m.estSystem && <MessageTranslation key={`${currentUsername}:${m.persistedId}`} messageId={m.persistedId} />}
                  {lounge && m.persistedId && !m.estSystem ? <button type="button" className="chat-message-translate" disabled={lounge.busy || lounge.pinnedIds.includes(m.persistedId)} onClick={() => lounge.pin(m.persistedId)}>{lounge.pinnedIds.includes(m.persistedId) ? <LocalizedText source={"Épinglé"} /> : <LocalizedText source={"Épingler"} />}</button> : null}
                  {onMessageAction && m.persistedId && m.contenu && !m.estSystem ? (
                    <button type="button" className="chat-message-translate" onClick={() => onMessageAction(m.persistedId)}>
                      Transformer en action
                    </button>
                  ) : null}
                  </MessageActions>
                  {m.media_url ? (
                    ["application/pdf", "document"].includes(m.media_type) ? (
                      <button type="button" onClick={() => downloadPracticalFile(m.media_url, m.media_type === "application/pdf" ? "document.pdf" : "document").catch(error => setUploadError(error.message))}><LocalizedText source={"Télécharger le document privé"} /></button>
                    ) : isAudioType(m.media_type, m.media_url) ? (
                      <audio src={resolveMediaUrl(m.media_url)} controls className="chat-media chat-media--audio" />
                    ) : isVideoType(m.media_type, m.media_url) ? (
                      <video src={resolveMediaUrl(m.media_url)} controls className="chat-media" />
                    ) : (
                      <LocalizedElement elementType={"button"} copyAttributes={["aria-label"]} type="button" className="chat-image-open" aria-label={sourceAttribute("Agrandir l’image")} onClick={() => setImageZoomUrl(resolveMediaUrl(m.media_url))}>
                        <LocalizedElement elementType={"img"} copyAttributes={["alt"]} src={resolveMediaUrl(m.media_url)} alt={sourceAttribute("Pièce jointe partagée")} className="chat-media" />
                      </LocalizedElement>
                    )
                  ) : null}
                  {m.gif_url ? (
                    <LocalizedElement elementType={"button"} copyAttributes={["aria-label"]} type="button" className="chat-image-open" aria-label={sourceAttribute("Agrandir le GIF")} onClick={() => setImageZoomUrl(resolveMediaUrl(m.gif_url))}>
                      <img src={resolveMediaUrl(m.gif_url)} alt="gif" className="chat-media" />
                    </LocalizedElement>
                  ) : null}
                  <div className="chat-message__meta">
                    <span className="chat-message__author">
                      {variant === "private-premium" && m.estSelf ? <LocalizedText source={"Vous"} /> : lounge?.nicknames[m.auteur] || m.auteur}
                    </span>
                    {m.timestamp && (
                      <span className="chat-message__time">
                        {formatHeure(m.timestamp)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        {showReactions ? (
          <ChatReactions
            onSendReaction={onSendReaction}
            incomingReactions={incomingReactions}
          />
        ) : null}
      </LocalizedElement>

      {/* ✨ Zone "quelqu’un écrit…" */}
      <div className="chat-panel-typing">
        {typingAffiches.length > 0 && (
          <>
            <span>
              {typingAffiches.length === 1
                ? <LocalizedText source={`${typingAffiches[0]} écrit…`} pattern={"__ET_VALUE_0__ écrit…"} />
                : <LocalizedText source={"Plusieurs personnes écrivent…"} />}
            </span>
            <span className="chat-panel-typing__dots">
              <span className="chat-panel-typing__dot" />
              <span className="chat-panel-typing__dot" />
              <span className="chat-panel-typing__dot" />
            </span>
          </>
        )}
      </div>

      {/* ✍️ Input */}
      <div className="chat-panel-input">
        <ChatGifPicker open={gifPickerOpen} onClose={() => setGifPickerOpen(false)} onSelect={gif => {
          setGifUrl(gif.url); setMedia(null); setMediaType(""); setVoiceStatus(""); setGifPickerOpen(false);
        }} />
        {media || gifUrl ? <LocalizedElement elementType={"div"} copyAttributes={["aria-label"]} className="chat-draft-media" aria-label={sourceAttribute("Pièce jointe à envoyer")}>
          <div className="chat-draft-media__heading">
            <strong>{gifUrl ? <LocalizedText source={"GIF prêt à envoyer"} /> : isAudioType(mediaType, media) ? <LocalizedText source={"Message vocal prêt à envoyer"} /> : <LocalizedText source={"Pièce jointe prête à envoyer"} />}</strong>
            <LocalizedElement elementType={"button"} copyAttributes={["aria-label"]} type="button" aria-label={sourceAttribute("Retirer la pièce jointe")} onClick={() => { setMedia(null); setMediaType(""); setGifUrl(""); setVoiceStatus(""); }}><LocalizedText source={"Retirer"} /></LocalizedElement>
          </div>
          {gifUrl ? <LocalizedElement elementType={"img"} copyAttributes={["alt"]} src={resolveMediaUrl(gifUrl)} alt={sourceAttribute("GIF sélectionné")} /> : isAudioType(mediaType, media) ? <LocalizedElement elementType={"audio"} copyAttributes={["aria-label"]} src={resolveMediaUrl(media)} controls preload="metadata" aria-label={sourceAttribute("Écouter le message vocal avant envoi")} /> : isVideoType(mediaType, media) ? <video src={resolveMediaUrl(media)} controls /> : mediaType === "application/pdf" ? <span>Document PDF</span> : <LocalizedElement elementType={"img"} copyAttributes={["alt"]} src={resolveMediaUrl(media)} alt={sourceAttribute("Pièce jointe sélectionnée")} />}
        </LocalizedElement> : null}
        {recordingVoice || uploading ? <p className="chat-draft-status" role="status">{recordingVoice ? <LocalizedText source={"● Enregistrement en cours… Arrête le vocal pour le réécouter."} /> : <LocalizedText source={"Préparation de la pièce jointe…"} />}</p> : null}
        {privatePeer && preparedReplies.length ? <label><LocalizedText source={"Réponse préparée"} /><LocalizedElement elementType={"select"} copyAttributes={["aria-label"]} aria-label={sourceAttribute("Insérer une réponse préparée")} value="" onChange={event => { const reply = preparedReplies.find(item => item.id === event.target.value); if (reply) setTexte(current => current ? `${current}\n${reply.text}` : reply.text); }}><LocalizedElement elementType={"option"} copyChildren value="">Choisir sans envoyer…</LocalizedElement>{preparedReplies.map(reply => <option key={reply.id} value={reply.id}>{reply.title}</option>)}</LocalizedElement></label> : null}
        <form className="chat-panel-input-inner" onSubmit={handleSubmit}>
          <LocalizedElement elementType={"textarea"} copyAttributes={["aria-label"]}
            className="chat-input"
            placeholder={placeholder}
            aria-label={sourceAttribute("Écrire un message")}
            disabled={sending}
            value={texte}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
          />
          <button
            type="submit"
            className="chat-send-btn"
            disabled={sending || (!texte.trim() && !media && !gifUrl) || uploading || recordingVoice}
          >
            <span className="chat-send-btn__icon">➤</span>
            <span><LocalizedText source={"Envoyer"} /></span>
          </button>
        </form>
        {collapsibleTools ? <div className="private-chat-composer-footer">
          <button type="button" className="private-chat-tools-toggle" aria-expanded={toolsExpanded} aria-controls={toolsId} onClick={() => setToolsExpanded(value => !value)}><span aria-hidden="true">{toolsExpanded ? "−" : "+"}</span><LocalizedText source={" Pièces jointes et outils"} /></button>
          <span><LocalizedText source={"Entrée pour envoyer "} /><span aria-hidden="true">↵</span></span>
        </div> : null}
        <div id={collapsibleTools ? toolsId : undefined} className="chat-panel-input-tools" hidden={collapsibleTools && !toolsExpanded}>
          {privatePeer ? <label className="chat-media-upload"><span><LocalizedText source={"Document privé (PDF)"} /></span><input type="file" accept="application/pdf" disabled={uploading} onChange={event => { const file = event.target.files?.[0]; event.target.value = ""; attachDocument(file); }} /></label> : null}
          <label className="chat-media-upload">
            <span aria-hidden="true">⌕</span>
            <span><LocalizedText source={"Photo, vidéo ou audio"} /></span>
            <LocalizedElement elementType={"input"} copyAttributes={["aria-label"]} type="file" aria-label={sourceAttribute("Photo, vidéo ou audio")} accept="image/*,video/*,audio/*,.gif" onChange={(e) => uploadMedia(e.target.files?.[0])} />
          </label>
          <button type="button" className="chat-voice-btn" onClick={startVoiceRecording} disabled={uploading} aria-pressed={recordingVoice}>
            {recordingVoice ? <LocalizedText source={"■ Arrêter le vocal"} /> : "● Message vocal"}
          </button>
          <label className="chat-translation-control">
            <span><LocalizedText source={"Traduire"} /></span>
            <select value={voiceLanguage} onChange={(event) => setVoiceLanguage(event.target.value)}>
              <LocalizedElement elementType={"option"} copyChildren value="en">Anglais</LocalizedElement>
              <LocalizedElement elementType={"option"} copyChildren value="es">Espagnol</LocalizedElement>
              <LocalizedElement elementType={"option"} copyChildren value="de">Allemand</LocalizedElement>
              <LocalizedElement elementType={"option"} copyChildren value="it">Italien</LocalizedElement>
            </select>
          </label>
          <button type="button" className="chat-voice-btn" onClick={translateDraft} disabled={!texte.trim()}><LocalizedText source={"Traduire le brouillon"} /></button>
          {uploading ? <span><LocalizedText source={"Téléversement..."} /></span> : null}
          {voiceStatus ? <span className="chat-voice-status">{voiceStatus}</span> : null}
          {uploadError ? <span className="chat-upload-error">{uploadError}</span> : null}
          <button type="button" className="chat-voice-btn" aria-expanded={gifPickerOpen} onClick={() => setGifPickerOpen(value => !value)} disabled={uploading || recordingVoice}>GIF</button>
        </div>
      </div>
      {imageZoomUrl ? <ChatImageLightbox src={imageZoomUrl} onClose={closeImageZoom} /> : null}
    </div>
  );
}

export default ChatPanel;
